import { isPublicGalleryPath } from '@/config/routes';
import { getScopeRole } from '@/services/current-context';

/**
 * Explicit light/dark theming, rolled out to INTERNAL_ADMIN only.
 *
 * The stored preference (localStorage `theme`) is per-browser, but the
 * `.dark` class is applied only while the current session's role is
 * INTERNAL_ADMIN — a customer user logging into the same browser always
 * gets light mode. There is intentionally no system-preference option.
 *
 * applyTheme() must run before React mounts (src/index.tsx) to avoid a
 * flash of the wrong theme, and again wherever `role` is written
 * (login callback) so a role change corrects the theme immediately.
 *
 * The public gallery (DV-3717) is light only, whoever's browser opens it: its
 * design has no dark variant, and the visitor is anonymous. It is checked here,
 * before React mounts, because the page is a lazy chunk: a fix inside it ran
 * only after the dark Suspense fallback had painted. Neither way between the
 * gallery and the app stays in one document (Share opens the gallery in a new
 * tab, and every link on it leaves the site), so each side gets its own run.
 */

const THEME_STORAGE_KEY = 'theme';
const DARK_MODE_ROLE = 'INTERNAL_ADMIN';

export type Theme = 'light' | 'dark';

export function getStoredTheme(): Theme {
  return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
}

export function setStoredTheme(theme: Theme): void {
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  applyTheme();
}

export function applyTheme(): void {
  const darkAllowed =
    getScopeRole() === DARK_MODE_ROLE &&
    !isPublicGalleryPath(window.location.pathname);
  const useDark = darkAllowed && getStoredTheme() === 'dark';
  document.documentElement.classList.toggle('dark', useDark);
}
